import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Select, Typography, Spin, message, Empty, Table, Tag, Tooltip,
  Button, Modal, Divider, Form, Input, Space,
} from 'antd';
import { AimOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { ventaService, ventaTransitoriaService } from '../../services/api';
import SelectComprador from './SelectComprador';
import { money } from '../../utils/format';

const { Text } = Typography;
const { Option } = Select;

// ── Modal de match: buscar comprador existente o crear uno nuevo ─────────────
const MatchModal = ({ target, onClose, onDone }) => {
  const [extraUsuarios, setExtraUsuarios] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reconciliando, setReconciliando] = useState(false);
  const [modoAlta, setModoAlta] = useState(false);
  const [form] = Form.useForm();
  const [creando, setCreando] = useState(false);

  // Al abrir: resetear estado. La pre-búsqueda con el comprador_texto libre la
  // dispara <SelectComprador> vía initialQuery.
  useEffect(() => {
    if (target) {
      setSelected(null);
      setModoAlta(false);
      form.resetFields();
      setExtraUsuarios([]);
    }
  }, [target, form]);

  const crearComprador = async () => {
    try {
      const values = await form.validateFields();
      setCreando(true);
      const res = await ventaService.registroRapido(values);
      const nuevo = res.data.usuario;
      message.success(res.data.message || 'Comprador creado');
      setExtraUsuarios((prev) => [nuevo, ...prev]);
      setSelected(nuevo.id_usuario);
      setModoAlta(false);
    } catch (err) {
      if (err?.errorFields) return;
      if (err?.response?.status === 409) {
        message.warning(err.response.data.error);
        const u = err.response.data.usuario;
        if (u) { setExtraUsuarios((prev) => [u, ...prev]); setSelected(u.id_usuario); setModoAlta(false); }
      } else {
        message.error(err?.response?.data?.error || 'Error al crear comprador');
      }
    } finally {
      setCreando(false);
    }
  };

  const reconciliar = async () => {
    if (!selected) { message.warning('Elegí o creá el comprador'); return; }
    setReconciliando(true);
    try {
      const res = await ventaTransitoriaService.reconciliar({ ids: target.ids, id_usuario: selected });
      const { reconciliados = 0, saltados = [] } = res.data || {};
      if (reconciliados) message.success(`${reconciliados} venta${reconciliados === 1 ? '' : 's'} reconciliada${reconciliados === 1 ? '' : 's'}`);
      if (saltados.length) message.warning(`${saltados.length} salteada(s) por superar el 100%`);
      if (!reconciliados && !saltados.length) message.info('No se reconcilió ninguna venta');
      onDone?.();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al reconciliar');
    } finally {
      setReconciliando(false);
    }
  };

  return (
    <Modal
      title="Match de comprador"
      open={!!target}
      onCancel={onClose}
      destroyOnHidden
      width={520}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancelar</Button>,
        <Button key="ok" type="primary" loading={reconciliando} disabled={!selected} onClick={reconciliar}>
          Reconciliar {target ? `(${target.ids.length})` : ''}
        </Button>,
      ]}
    >
      {target && (
        <div style={{ paddingTop: 8 }}>
          <Text type="secondary">
            Vas a pasar <strong>{target.ids.length}</strong> asiento(s) de{' '}
            «<strong>{target.comprador_texto}</strong>» a ventas definitivas.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Comprador real</Text>
            <SelectComprador
              value={selected}
              onChange={setSelected}
              placeholder="Buscar comprador de la cabaña..."
              extraUsuarios={extraUsuarios}
              initialQuery={target.comprador_texto || ''}
            />
          </div>

          {!modoAlta ? (
            <Button type="link" icon={<UserAddOutlined />} style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => setModoAlta(true)}>
              El comprador no está — darlo de alta
            </Button>
          ) : (
            <>
              <Divider style={{ margin: '12px 0' }}>Nuevo comprador</Divider>
              <Form form={form} layout="vertical" initialValues={{ nom_usuario: target.comprador_texto }}>
                <Form.Item name="nom_usuario" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
                  <Input placeholder="Razón social / nombre" />
                </Form.Item>
                <Form.Item name="email_usuario" label="Email" rules={[{ required: true, message: 'Requerido' }, { type: 'email', message: 'Email inválido' }]}>
                  <Input placeholder="comprador@ejemplo.com" />
                </Form.Item>
                <Form.Item name="cuit" label="CUIT / DNI" rules={[{ required: true, message: 'Requerido' }]} extra="Se usa como contraseña inicial">
                  <Input placeholder="20123456789" maxLength={11} />
                </Form.Item>
                <Space>
                  <Button type="primary" icon={<PlusOutlined />} loading={creando} onClick={crearComprador}>Crear y elegir</Button>
                  <Button onClick={() => setModoAlta(false)}>Cancelar</Button>
                </Space>
              </Form>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

// ── Solapa Match de venta ────────────────────────────────────────────────────
const MatchVenta = ({ eventos = [], loadingEventos = false }) => {
  const [eventoId, setEventoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [target, setTarget] = useState(null);

  const cargar = useCallback(async () => {
    if (!eventoId) { setPendientes([]); return; }
    setLoading(true);
    try {
      const res = await ventaTransitoriaService.pendientes(eventoId);
      setPendientes(res.data || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar los pendientes de match');
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar por comprador_texto → filas padre con hijos (animales).
  const groups = useMemo(() => {
    const byComprador = {};
    pendientes.forEach((p) => {
      const key = p.comprador_texto || '(sin comprador)';
      (byComprador[key] = byComprador[key] || []).push(p);
    });
    return Object.entries(byComprador)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([comprador, items]) => ({
        isGroup: true,
        comprador,
        ids: items.map((i) => i.id_venta_transitoria),
        count: items.length,
        total: items.reduce((s, i) => s + (Number(i.importe_venta) || 0), 0),
        children: items,
      }));
  }, [pendientes]);

  const columns = [
    {
      title: 'Comprador / Animal', dataIndex: 'comprador',
      render: (_, r) => r.isGroup
        ? <Space size={6}><Text strong>{r.comprador}</Text><Tag color="gold">{r.count}</Tag></Space>
        : <span>{r.rp_animal} — {r.nom_animal}</span>,
    },
    { title: 'Corral', dataIndex: 'corral', width: 90, render: (_, r) => r.isGroup ? null : (r.corral ?? '—') },
    { title: '%', dataIndex: 'porcentaje_venta', width: 70, render: (_, r) => r.isGroup ? null : `${r.porcentaje_venta}%` },
    {
      title: 'Precio', dataIndex: 'importe_venta', width: 150,
      render: (_, r) => r.isGroup ? <Text strong>{money(r.total)}</Text> : money(r.importe_venta),
    },
    {
      title: '', key: 'accion', width: 80, align: 'center',
      render: (_, r) => {
        const t = r.isGroup
          ? { comprador_texto: r.comprador, ids: r.ids }
          : { comprador_texto: r.comprador_texto, ids: [r.id_venta_transitoria] };
        const title = r.isGroup ? 'Matchear comprador (todos)' : 'Matchear este animal';
        return (
          <Tooltip title={title}>
            <Button type="primary" ghost size="small" icon={<AimOutlined />} onClick={() => setTarget(t)} />
          </Tooltip>
        );
      },
    },
  ];

  const totalAnimales = pendientes.length;
  const totalCompradores = groups.length;

  return (
    <div>
      <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Select
            loading={loadingEventos}
            placeholder="Seleccioná un evento..."
            value={eventoId}
            onChange={setEventoId}
            style={{ minWidth: 300 }}
            allowClear
          >
            {eventos.map((e) => (
              <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>
            ))}
          </Select>
        </Col>
        {eventoId && totalAnimales > 0 && (
          <Col><Text type="secondary">{totalCompradores} compradores · {totalAnimales} pendientes</Text></Col>
        )}
      </Row>

      {loading && <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>}
      {!loading && !eventoId && <Empty description="Seleccioná un evento para reconciliar sus ventas transitorias" />}
      {!loading && eventoId && totalAnimales === 0 && <Empty description="No hay ventas transitorias pendientes en este evento" />}

      {!loading && eventoId && totalAnimales > 0 && (
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={groups}
          columns={columns}
          rowKey={(r) => (r.isGroup ? `g-${r.comprador}` : `a-${r.id_venta_transitoria}`)}
          size="small"
          pagination={false}
          expandable={{ defaultExpandAllRows: false }}
        />
      )}

      <MatchModal target={target} onClose={() => setTarget(null)} onDone={cargar} />
    </div>
  );
};

export default MatchVenta;
